import {
  Autocomplete,
  Alert,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  ToggleButton,
  ToggleButtonGroup,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getOriginalFileName } from "utils/utils";
import {
  fetchEmailTemplates,
  sendAdmissionDocumentsEmail,
} from "services/admissionDocumentEmailApi";
import { fetchLinkedDocuments } from "views/admin/Clients/documents/clientDocuments.api";
import PermanentLinksManager from "./PermanentLinksManager";
import { mergeNotifyStudentRecipient } from "./emailRecipientUtils";
import { sendWhatsAppBulk } from "api/whatsapp";
import { P } from "const/permissions";
import { hasPermission } from "utils/permissionUtils";
import { htmlToPlainText, phoneDigits } from "utils/whatsappMessage";

// Presigned links cannot outlive 7 days (S3 SigV4 cap) — offer sane presets up to that.
const EXPIRY_OPTIONS = [
  { label: "2 minutes (testing)", value: 120 },
  { label: "1 hour", value: 3600 },
  { label: "1 day", value: 86400 },
  { label: "3 days", value: 259200 },
  { label: "1 week (max)", value: 604800 },
];

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// The server wraps the message in a branded layout and appends the delivery notice
// (link expiry / attachments) plus a "Kind regards, <sender>, <organization>" signature —
// so the defaults are mode-specific and deliberately signature-free. `clientRef` is
// "Name (Student ID: X)" so the college knows exactly whose documents these are.
const buildDefaultSubject = (clientRef) =>
  clientRef ? `Supporting documents for ${clientRef}` : "Supporting documents";
const buildDefaultBodies = (clientRef) => {
  const forRef = clientRef ? ` for <b>${escapeHtml(clientRef)}</b>` : "";
  return {
    PRESIGNED: `Hello,<br/><br/>Please find the supporting documents${forRef} below. Note that the download links are time-limited — see the expiry notice in this email.<br/><br/>{documentLinks}`,
    PERMANENT: `Hello,<br/><br/>Please find the supporting documents${forRef} below.<br/><br/>{documentLinks}`,
    ATTACH: `Hello,<br/><br/>Please find the supporting documents${forRef} attached to this email.`,
  };
};

const isEmail = (value) => /\S+@\S+\.\S+/.test(String(value || "").trim());

const EmailChips = ({ label, value, onChange, placeholder }) => (
  <Autocomplete
    multiple
    freeSolo
    options={[]}
    value={value}
    onChange={(_, next) => onChange(next.map((v) => v.trim()).filter(Boolean))}
    renderTags={(items, getTagProps) =>
      items.map((item, index) => (
        <Chip
          size="small"
          label={item}
          color={isEmail(item) ? "default" : "error"}
          {...getTagProps({ index })}
          key={item + index}
        />
      ))
    }
    renderInput={(params) => (
      <TextField {...params} label={label} placeholder={placeholder} size="small" />
    )}
  />
);

const EmailDocumentsDialog = ({
  open,
  onClose,
  studentId,
  documents = [],
  universityId,
  universityName,
  universityEmail,
  clientName,
  clientStudentId,
  clientEmail,
  clientId,
  clientPhone,
}) => {
  const clientRef = useMemo(() => {
    const name = String(clientName || "").trim();
    const sid = String(clientStudentId || "").trim();
    if (!name) return "";
    return sid ? `${name} (Student ID: ${sid})` : name;
  }, [clientName, clientStudentId]);
  const defaultSubject = useMemo(() => buildDefaultSubject(clientRef), [clientRef]);
  const defaultBodies = useMemo(() => buildDefaultBodies(clientRef), [clientRef]);
  const defaultBodyFor = (nextMode) => defaultBodies[nextMode] ?? defaultBodies.PRESIGNED;
  const isDefaultBody = (value) => Object.values(defaultBodies).includes(value);

  const [selectedUrls, setSelectedUrls] = useState([]);
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [includeUniversityDefault, setIncludeUniversityDefault] = useState(true);
  const [mode, setMode] = useState("PERMANENT");
  const [expirySeconds, setExpirySeconds] = useState(604800);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBodies.PERMANENT);
  const [notifyStudent, setNotifyStudent] = useState(false);
  const [notifyPlacement, setNotifyPlacement] = useState("CC");
  const [sending, setSending] = useState(false);
  const [channel, setChannel] = useState("email");
  const [recordOptIn, setRecordOptIn] = useState(false);
  const whatsappNumber = phoneDigits(clientPhone);
  const canWhatsApp =
    hasPermission(P.MESSAGING.WHATSAPP_SEND) && Boolean(clientId) && Boolean(whatsappNumber);

  const trimmedClientEmail = useMemo(() => String(clientEmail || "").trim(), [clientEmail]);
  const hasClientEmail = isEmail(trimmedClientEmail);
  const notifyStudentBlocked = notifyStudent && !hasClientEmail;

  // Documents shown on this admission via "Show in module" never land in the admission's own
  // saved documents -- they stay linked ClientDocument rows. Offer them here too, so staff don't
  // have to re-upload something that is already sitting on the record.
  const { data: linkedDocumentsData } = useQuery(
    ["email-documents-linked-admission", studentId],
    () => fetchLinkedDocuments("ADMISSION", studentId),
    { enabled: open && !!studentId }
  );
  const linkedDocuments = useMemo(() => {
    const nativeUrls = new Set(documents.map((d) => d.url).filter(Boolean));
    return (linkedDocumentsData || [])
      .filter((d) => d?.url && !nativeUrls.has(d.url))
      .map((d) => ({
        id: `linked-${d.documentId}`,
        name: d.originalFilename,
        url: d.url,
        addedAt: d.uploadedAt,
        linked: true,
      }));
  }, [documents, linkedDocumentsData]);
  const allDocuments = useMemo(
    () => [...documents, ...linkedDocuments],
    [documents, linkedDocuments]
  );

  useEffect(() => {
    if (!open) return;
    setSelectedUrls(documents.map((d) => d.url).filter(Boolean));
    setTo([]);
    setCc([]);
    setBcc([]);
    setIncludeUniversityDefault(true);
    setNotifyStudent(false);
    setNotifyPlacement("CC");
    setChannel("email");
    setRecordOptIn(false);
    setMode("PERMANENT");
    setExpirySeconds(604800);
    setTemplateId("");
    setSubject(defaultSubject);
    setBody(defaultBodies.PERMANENT);
    fetchEmailTemplates()
      .then((list) => setTemplates(Array.isArray(list) ? list : []))
      .catch(() => setTemplates([]));
  }, [open, documents]);

  useEffect(() => {
    if (!open || linkedDocuments.length === 0) return;
    setSelectedUrls((prev) => {
      const missing = linkedDocuments.map((d) => d.url).filter((u) => !prev.includes(u));
      return missing.length ? [...prev, ...missing] : prev;
    });
  }, [open, linkedDocuments]);

  useEffect(() => {
    if (!open || !isEmail(universityEmail)) return;
    setTo((prev) => (prev.length ? prev : [universityEmail.trim()]));
    setIncludeUniversityDefault(false);
  }, [open, documents, universityEmail]);

  const canSendUniversity = includeUniversityDefault && !!universityId && !isEmail(universityEmail);
  const recipientCount = useMemo(
    () => to.filter(isEmail).length + (canSendUniversity ? 1 : 0),
    [to, canSendUniversity]
  );

  const toggleDoc = (url) =>
    setSelectedUrls((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]
    );

  const applyTemplate = (id) => {
    setTemplateId(id);
    const tpl = templates.find((t) => t.id === id);
    if (tpl) {
      setSubject(tpl.subject || defaultSubject);
      setBody(tpl.body || defaultBodyFor(mode));
    }
  };

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setBody((prev) => (isDefaultBody(prev) ? defaultBodyFor(nextMode) : prev));
  };

  const handleSend = async () => {
    const selectedDocs = allDocuments.filter((d) => selectedUrls.includes(d.url));
    if (selectedDocs.length === 0) {
      toast.error("Select at least one document.");
      return;
    }
    if (channel === "whatsapp") {
      if (!canWhatsApp) {
        toast.error("WhatsApp needs this client's mobile number, with its country code");
        return;
      }
      if (!recordOptIn) {
        toast.error("Confirm that this client has opted in to WhatsApp");
        return;
      }
      const links = [];
      const media = [];
      selectedDocs.forEach((doc) => {
        const url = String(doc.url || "");
        if (/^https?:\/\//i.test(url)) {
          links.push(`${doc.name || "Document"}: ${url}`);
        } else if (url) {
          media.push({
            storageKey: url,
            fileName: doc.name || getOriginalFileName(url) || "file",
          });
        }
      });
      const text = [subject, htmlToPlainText(body), links.join("\n")].filter(Boolean).join("\n\n");
      setSending(true);
      try {
        const response = await sendWhatsAppBulk({
          clientIds: [clientId],
          text,
          recordOptIn: true,
          subject,
          source: "ADMISSION_DOCUMENTS",
          media,
        });
        const result = response?.data?.data;
        if (result?.batchStatus === "FAILED") {
          toast.error(result.results?.[0]?.errorMessage || "WhatsApp could not send the documents");
          return;
        }
        toast.success("Documents sent on WhatsApp");
        onClose();
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to send the WhatsApp message");
      } finally {
        setSending(false);
      }
      return;
    }
    if (recipientCount === 0) {
      toast.error(
        isEmail(universityEmail)
          ? "Add at least one recipient."
          : "Add at least one recipient (or enable the university default)."
      );
      return;
    }
    const invalid = [...to, ...cc, ...bcc].filter((e) => e && !isEmail(e));
    if (invalid.length) {
      toast.error(`Invalid email: ${invalid[0]}`);
      return;
    }

    setSending(true);
    try {
      const toRecipients = to.filter(isEmail);
      const ccRecipients = cc.filter(isEmail);
      const bccRecipients = bcc.filter(isEmail);
      const { cc: finalCc, bcc: finalBcc } =
        notifyStudent && hasClientEmail
          ? mergeNotifyStudentRecipient({
              to: toRecipients,
              cc: ccRecipients,
              bcc: bccRecipients,
              studentEmail: trimmedClientEmail,
              placement: notifyPlacement,
            })
          : { cc: ccRecipients, bcc: bccRecipients };

      const response = await sendAdmissionDocumentsEmail(studentId, {
        documents: selectedDocs.map((d) => ({ name: d.name, url: d.url })),
        toRecipients,
        ccRecipients: finalCc,
        bccRecipients: finalBcc,
        deliveryMode: mode,
        expirySeconds: mode === "PRESIGNED" ? expirySeconds : undefined,
        universityId: universityId ?? undefined,
        includeUniversityDefault,
        templateId: templateId || undefined,
        subject,
        body,
      });
      const result = response?.data ?? {};
      const failed = Number(result.failedCount) || 0;
      const succeeded = Number(result.successCount) || 0;
      if (failed > 0 && succeeded === 0) {
        const firstError = result.results?.find((r) => r.errorMessage)?.errorMessage;
        toast.error(
          `Failed to send to all ${failed} recipient(s).${firstError ? ` ${firstError}` : ""}`
        );
        return;
      }
      if (failed > 0) {
        toast.warning(
          `Sent to ${succeeded} of ${succeeded + failed} recipients — ${failed} failed.`
        );
      } else {
        toast.success("Documents emailed.");
      }
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to send documents email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle className="font-semibold text-app-text">
        {channel === "whatsapp" ? "WhatsApp documents" : "Email documents"}
      </DialogTitle>
      <DialogContent className="space-y-4 pt-1">
        {canWhatsApp && (
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={channel}
            onChange={(_, next) => next && setChannel(next)}
          >
            <ToggleButton value="email">Email</ToggleButton>
            <ToggleButton value="whatsapp">WhatsApp</ToggleButton>
          </ToggleButtonGroup>
        )}
        {channel === "whatsapp" && (
          <>
            <Typography variant="body2" className="text-app-text-secondary">
              Sends to {clientName || "this client"} at {clientPhone}. Files go as separate
              messages. Links are written into the message. No WhatsApp group is created.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={recordOptIn}
                  onChange={(event) => setRecordOptIn(event.target.checked)}
                />
              }
              label="This client has opted in to WhatsApp"
            />
          </>
        )}
        {/* Recipients */}
        {universityId ? (
          isEmail(universityEmail) ? (
            <Typography variant="caption" className="block text-app-text-secondary">
              {to.includes(universityEmail.trim())
                ? `${
                    universityName || "University"
                  } contact (${universityEmail}) is prefilled in To — remove it if this email should not go to the university.`
                : `${
                    universityName || "University"
                  } contact (${universityEmail}) was removed — the university will not receive this email.`}
            </Typography>
          ) : (
            <FormControlLabel
              control={
                <Switch
                  checked={includeUniversityDefault}
                  onChange={(e) => setIncludeUniversityDefault(e.target.checked)}
                />
              }
              label={
                <span>
                  Send to university default{universityName ? ` — ${universityName}` : ""} (contact
                  email looked up on send)
                </span>
              }
            />
          )
        ) : (
          <Alert severity="info">
            No university selected in Course Details — add recipients manually below.
          </Alert>
        )}
        <EmailChips
          label="To"
          value={to}
          onChange={setTo}
          placeholder="Add recipient email + Enter"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <EmailChips label="Cc" value={cc} onChange={setCc} placeholder="Cc email + Enter" />
          <EmailChips label="Bcc" value={bcc} onChange={setBcc} placeholder="Bcc email + Enter" />
        </div>

        <FormControlLabel
          control={
            <Switch checked={notifyStudent} onChange={(e) => setNotifyStudent(e.target.checked)} />
          }
          label="Notify student"
        />
        {notifyStudent && (
          <>
            {hasClientEmail ? (
              <FormControl>
                <FormLabel className="text-sm text-app-text-secondary">
                  Send copy to student ({trimmedClientEmail}) as
                </FormLabel>
                <RadioGroup
                  row
                  value={notifyPlacement}
                  onChange={(e) => setNotifyPlacement(e.target.value)}
                >
                  <FormControlLabel value="CC" control={<Radio size="small" />} label="Cc" />
                  <FormControlLabel value="BCC" control={<Radio size="small" />} label="Bcc" />
                </RadioGroup>
              </FormControl>
            ) : (
              <Alert severity="warning">Email not set in client details for this student</Alert>
            )}
          </>
        )}

        <Divider />

        {/* Documents */}
        <div>
          <Typography variant="subtitle2" className="text-app-text mb-1">
            Documents ({selectedUrls.length}/{allDocuments.length})
          </Typography>
          {allDocuments.length === 0 ? (
            <Alert severity="warning">No documents to send. Add and save documents first.</Alert>
          ) : (
            <div className="max-h-40 overflow-y-auto rounded border border-app-border">
              {allDocuments.map((doc) => (
                <label
                  key={doc.id || doc.url}
                  className="flex items-center gap-2 px-2 py-1 border-b border-app-border last:border-b-0 cursor-pointer"
                >
                  <Checkbox
                    size="small"
                    checked={selectedUrls.includes(doc.url)}
                    onChange={() => toggleDoc(doc.url)}
                  />
                  <span className="text-sm text-app-text truncate">
                    {doc.name || getOriginalFileName(doc.url)}
                  </span>
                  {doc.linked && (
                    <Chip
                      label="Linked"
                      size="small"
                      variant="outlined"
                      className="ml-auto shrink-0"
                    />
                  )}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Delivery mode */}
        <FormControl>
          <FormLabel className="text-sm">How to send</FormLabel>
          <RadioGroup row value={mode} onChange={(e) => handleModeChange(e.target.value)}>
            <FormControlLabel
              value="PRESIGNED"
              control={<Radio size="small" />}
              label="Expiring link"
            />
            <FormControlLabel
              value="PERMANENT"
              control={<Radio size="small" />}
              label="Permanent link"
            />
            <FormControlLabel
              value="ATTACH"
              control={<Radio size="small" />}
              label="Attach files"
            />
          </RadioGroup>
        </FormControl>
        {mode === "PRESIGNED" && (
          <TextField
            select
            label="Link expires in"
            size="small"
            value={expirySeconds}
            onChange={(e) => setExpirySeconds(Number(e.target.value))}
            sx={{ maxWidth: 220 }}
          >
            {EXPIRY_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        )}
        {mode === "ATTACH" && (
          <Alert severity="info">
            Files are attached to the email — total size is limited to ~7.5 MB (email provider cap);
            larger selections are rejected before sending.
          </Alert>
        )}
        {mode === "PERMANENT" && (
          <>
            <Alert severity="info">
              Stable links that never expire; revoke them below at any time. Links resolve on click
              and die automatically if the document is removed from this admission.
            </Alert>
            <PermanentLinksManager studentId={studentId} />
          </>
        )}

        <Divider />

        {/* Template + message */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <TextField
            select
            label="Template (optional)"
            size="small"
            value={templateId}
            onChange={(e) => applyTemplate(e.target.value)}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {templates.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.templateName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Subject"
            size="small"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>
        <TextField
          label="Message (HTML). Use {documentLinks} where the links should appear."
          value={body}
          onChange={(e) => setBody(e.target.value)}
          fullWidth
          multiline
          minRows={4}
          size="small"
          helperText="Sent inside a branded layout — the link-expiry/attachment notice and a signature (Kind regards, your name & organization) are added automatically. Template placeholders {clientRef}, {clientName} and {studentId} are filled with this admission's client on send."
        />
      </DialogContent>
      <DialogActions className="px-4 pb-3">
        <Button onClick={onClose} color="inherit" disabled={sending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSend}
          disabled={
            sending || allDocuments.length === 0 || (channel !== "whatsapp" && notifyStudentBlocked)
          }
          startIcon={sending ? <CircularProgress size={16} /> : null}
        >
          {sending
            ? "Sending…"
            : channel === "whatsapp"
            ? "Send WhatsApp"
            : `Send${recipientCount ? ` (${recipientCount})` : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailDocumentsDialog;
